import { Box, FormGroup, Icon, Radio, Text, Tooltip } from '@ksyos/design-system';
import { YesOrNoOrStoppedOrUnknown } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { getValidationErrorTranslation, type ValidationErrors } from '@frontend/utils';
import {
    NumberInputContext,
    RadioGroupFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '../../../processActivityFormGroupFields';

type SmokingFormGroupProps = {
    data: {
        isSmoker: YesOrNoOrStoppedOrUnknown | null;
        packsADaySmoked: number | null;
        smokingStoppedSince: string | null;
        yearsSmoked: number | null;
    };
    errors: ValidationErrors;
    onChange: (data: Partial<SmokingFormGroupProps['data']>, validateKeys?: string | string[] | undefined) => void;
};

export function SmokingFormGroup(props: Readonly<SmokingFormGroupProps>) {
    const { data, errors, onChange } = props;
    const translate = getSelectedLanguage();

    return (
        <>
            <RadioGroupFormGroup
                error={getValidationErrorTranslation(errors.get('isSmoker'))}
                fieldKey="isSmoker"
                hasErrors={errors.has('isSmoker') || errors.has('yearsSmoked') || errors.has('packsADaySmoked')}
                label="Roken"
                onChange={async (value) => {
                    onChange(
                        {
                            isSmoker: value as YesOrNoOrStoppedOrUnknown,
                            ...([YesOrNoOrStoppedOrUnknown.no].includes(value as YesOrNoOrStoppedOrUnknown)
                                ? { isSmokerRemark: null }
                                : {}),
                            ...([YesOrNoOrStoppedOrUnknown.stopped].includes(value as YesOrNoOrStoppedOrUnknown)
                                ? { packsADaySmoked: null, yearsSmoked: null }
                                : {}),
                            ...([YesOrNoOrStoppedOrUnknown.no, YesOrNoOrStoppedOrUnknown.yes].includes(
                                value as YesOrNoOrStoppedOrUnknown
                            )
                                ? { smokingStoppedSince: null }
                                : {}),
                            ...([YesOrNoOrStoppedOrUnknown.unknown].includes(value as YesOrNoOrStoppedOrUnknown)
                                ? {
                                      isSmokerRemark: null,
                                      packsADaySmoked: null,
                                      smokingStoppedSince: null,
                                      yearsSmoked: null,
                                  }
                                : {}),
                        },
                        'isSmoker'
                    );
                }}
                sx={{ mb: 3 }}
                value={data.isSmoker ?? ''}
            >
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.yes}
                    label={YesOrNoOrStoppedOrUnknown.yes}
                    value={YesOrNoOrStoppedOrUnknown.yes}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.no}
                    label={YesOrNoOrStoppedOrUnknown.no}
                    value={YesOrNoOrStoppedOrUnknown.no}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.stopped}
                    label={YesOrNoOrStoppedOrUnknown.stopped}
                    value={YesOrNoOrStoppedOrUnknown.stopped}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.unknown}
                    label={YesOrNoOrStoppedOrUnknown.unknown}
                    value={YesOrNoOrStoppedOrUnknown.unknown}
                />
            </RadioGroupFormGroup>

            {data.isSmoker === YesOrNoOrStoppedOrUnknown.yes && (
                <FormGroup label="Hoeveelheid">
                    <Box
                        display="grid"
                        gridTemplateColumns="max-content max-content max-content"
                        sx={{ columnGap: 8 }}
                    >
                        <Box
                            display="grid"
                            gridTemplateColumns="80px max-content"
                        >
                            <NumberInputContext
                                fieldKey={'yearsSmoked'}
                                maximumFractionDigits={2}
                            />
                            <Text
                                fontWeight="medium"
                                ml={3}
                                mt={2}
                                size="sm"
                            >
                                jaar
                            </Text>
                        </Box>
                        <Box
                            display="grid"
                            gridTemplateColumns="80px max-content"
                        >
                            <NumberInputContext
                                fieldKey={'packsADaySmoked'}
                                maximumFractionDigits={2}
                            />
                            <Box
                                display="flex"
                                gap={2}
                                ml={3}
                                mt={2}
                            >
                                <Text
                                    fontWeight="medium"
                                    size="sm"
                                >
                                    pakjes per dag
                                    <Tooltip
                                        content="Op een typische dag van consumptie"
                                        placement="bottom"
                                    >
                                        <Icon
                                            color="primary"
                                            icon="information"
                                            sx={{ ml: 2 }}
                                        />
                                    </Tooltip>
                                </Text>
                            </Box>
                        </Box>
                        <Text
                            color="minimal"
                            fontWeight="medium"
                            mt={2}
                            size="sm"
                        >
                            {data.packsADaySmoked && data.yearsSmoked
                                ? `${data.packsADaySmoked * data.yearsSmoked} ${translate.units.packYears}`
                                : translate.units.packYears}
                        </Text>
                    </Box>
                </FormGroup>
            )}

            {data.isSmoker === YesOrNoOrStoppedOrUnknown.stopped && (
                <TextInputFormGroup
                    fieldKey="smokingStoppedSince"
                    label="Gestopt sinds"
                />
            )}

            {[YesOrNoOrStoppedOrUnknown.stopped, YesOrNoOrStoppedOrUnknown.yes].includes(
                data.isSmoker as YesOrNoOrStoppedOrUnknown
            ) && (
                <TextAreaFormGroup
                    fieldKey="isSmokerRemark"
                    label="Toelichting"
                />
            )}
        </>
    );
}
